'use client';

import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { PageHeader } from '@/components/layout/PageHeader';
import { usePageHeaderStore } from '@/components/layout/pageHeaderStore';
import { Button } from '@/components/ui/Button';
import { CardGridSkeleton } from '@/components/ui/CardGridSkeleton';
import { useSkills } from '../hooks/useSkills';
import { useSkillsStore } from '..';
import { SkillCard } from '../components/SkillCard';
import { SkillDetailPanel } from '../components/SkillDetailPanel';
import { SkillUploadDialog } from '../components/SkillUploadDialog';

export function SkillsPage() {
  const t = useTranslations('skills');
  const { skills, loading, error } = useSkills();
  const selectedSkillId = useSkillsStore((s) => s.selectedSkillId);
  const selectSkill = useSkillsStore((s) => s.actions.selectSkill);
  const [uploadOpen, setUploadOpen] = useState(false);

  // Set the chrome breadcrumb trail (Skills › {name}) while a skill detail panel
  // is open. The sourceKey guards against stale-unmount trail wipes.
  const setTrail = usePageHeaderStore((s) => s.actions.setTrail);
  const clearTrail = usePageHeaderStore((s) => s.actions.clearTrail);
  const selectedSkillName = skills.find((s) => s.id === selectedSkillId)?.name;
  useEffect(() => {
    if (!selectedSkillName) {
      clearTrail('skills');
      return;
    }
    setTrail('skills', [{ label: selectedSkillName }]);
    return () => clearTrail('skills');
  }, [selectedSkillName, setTrail, clearTrail]);

  // Group by category.
  const grouped = skills.reduce<Record<string, typeof skills>>((acc, skill) => {
    const cat = skill.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(skill);
    return acc;
  }, {});

  return (
    <div className="flex h-full overflow-hidden">
      {/* Browse panel */}
      <div className="flex-1 overflow-auto">
        {/* Create Skill in `actions`; identity ("Skills") lives in the chrome. */}
        <PageHeader
          actions={
            <Button variant="secondary" size="sm" onClick={() => setUploadOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              {t('skillsPage.createSkill')}
            </Button>
          }
        />

        <div className="p-6 space-y-6">
        {loading && <CardGridSkeleton count={6} />}

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {!loading && !error && Object.keys(grouped).length === 0 && (
          <div className="text-sm text-muted-foreground">{t('skillsPage.noSkills')}</div>
        )}

        {!loading && !error && Object.entries(grouped).map(([category, categorySkills]) => (
          <section key={category}>
            <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3 capitalize">
              {category}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {categorySkills.map((skill) => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  selected={selectedSkillId === skill.id}
                  onSelect={() => selectSkill(selectedSkillId === skill.id ? null : skill.id)}
                />
              ))}
            </div>
          </section>
        ))}
        </div>
      </div>

      {/* Detail panel */}
      {selectedSkillId && (
        <SkillDetailPanel
          skillId={selectedSkillId}
          onClose={() => selectSkill(null)}
        />
      )}

      {uploadOpen && (
        <SkillUploadDialog mode="create" onClose={() => setUploadOpen(false)} />
      )}
    </div>
  );
}
